import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { IconButton } from '@components/iconButton';
import { Button } from '@components/button';
import { Tooltip } from '@components/tooltip/Tooltip';
import { Select } from '@components/select';
import { useUpdateQuote } from '@services/quotes';
import { enqueueSnackbar } from 'notistack';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import type { ReportingQuoteSource } from '@lio/api-types';
import { ReportingQuoteSourceLabels, REPORTING_QUOTE_SOURCE_NONE_FILTER } from '@models/quotes';

import { PenSolid } from '@assets/svgs';
import { ToastType } from '@components/toast';

// Reuse the shared backend sentinel so the editor stays aligned with the filters.
const NONE_VALUE = REPORTING_QUOTE_SOURCE_NONE_FILTER;

const selectItems = [
  { key: NONE_VALUE, value: NONE_VALUE, label: '(None)' },
  ...Object.entries(ReportingQuoteSourceLabels).map(([value, label]) => ({
    key: value,
    value,
    label,
  })),
];

interface EditableQuoteSourceProps {
  quoteId: string;
  reportingQuoteSource: ReportingQuoteSource | null | undefined;
}

export const EditableQuoteSource = ({ quoteId, reportingQuoteSource }: EditableQuoteSourceProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>(NONE_VALUE);
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { updateQuote, isPending } = useUpdateQuote();

  const canEdit = !!isInternalUser;
  const displayValue = reportingQuoteSource ? ReportingQuoteSourceLabels[reportingQuoteSource] : '-';

  const handleStartEdit = () => {
    setEditValue(reportingQuoteSource ?? NONE_VALUE);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    const newValue = editValue === NONE_VALUE ? null : (editValue as ReportingQuoteSource);

    try {
      await updateQuote({
        quoteId,
        reportingQuoteSource: newValue,
      });

      enqueueSnackbar('Quote Source updated successfully', {
        variant: ToastType.Success,
        autoHideDuration: 3000,
      });

      setIsEditing(false);
    } catch (error: any) {
      let errorMessage = 'Failed to update Quote Source';

      try {
        const data = await error?.response?.json();
        if (data?.error?.message) {
          errorMessage = data.error.message;
        }
      } catch {
        if (error?.message) {
          errorMessage = error.message;
        }
      }

      enqueueSnackbar(errorMessage, {
        variant: ToastType.Error,
        autoHideDuration: 5000,
      });
    }
  };

  return (
    <Box
      component='span'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        m: 0,
        minWidth: 0,
        width: '100%',
        alignItems: 'flex-start',
      }}
    >
      <Typography
        component='span'
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.subdued',
        }}
      >
        Quote Source
      </Typography>

      {isEditing ? (
        <Box component='span' sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Select
            items={selectItems}
            value={editValue}
            onChangeSelect={(value) => setEditValue(value)}
            disabled={isPending}
            size='small'
            inputProps={{ 'aria-label': 'Quote Source' }}
          />
          <Box component='span' sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={isPending}
              size='small'
              sx={{
                padding: 0,
              }}
            >
              Save
            </Button>
            <Button
              variant='text'
              onClick={handleCancel}
              disabled={isPending}
              size='small'
              sx={{
                padding: 0,
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            component='span'
            variant='bodySmall'
            sx={{
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4,
            }}
          >
            {displayValue}
          </Typography>
          {canEdit && (
            <Tooltip title='Edit Quote Source'>
              <IconButton aria-label='Edit Quote Source' onClick={handleStartEdit} sx={{ padding: '2px' }}>
                <PenSolid width='12px' height='12px' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};
